import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Landmark, Plus, Trash2, Pencil, X, Info } from "lucide-react";
import { toast } from "sonner";

const cardTones = [
  { ring: "ring-emerald-200", chip: "bg-emerald-100 text-emerald-700", accent: "from-emerald-50" },
  { ring: "ring-amber-200", chip: "bg-amber-100 text-amber-700", accent: "from-amber-50" },
  { ring: "ring-sky-200", chip: "bg-sky-100 text-sky-700", accent: "from-sky-50" },
  { ring: "ring-violet-200", chip: "bg-violet-100 text-violet-700", accent: "from-violet-50" },
  { ring: "ring-rose-200", chip: "bg-rose-100 text-rose-700", accent: "from-rose-50" },
  { ring: "ring-teal-200", chip: "bg-teal-100 text-teal-700", accent: "from-teal-50" },
];

const emptyForm = { name: "", extra_fields: [] };

export default function Mosques() {
  const { user } = useAuth();
  const canManage = user.role === "admin" || user.role === "supervisor";
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/mosques").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ name: m.name, extra_fields: (m.extra_fields || []).map((f) => ({ ...f })) }); setOpen(true); };

  const addField = () => setForm((f) => ({ ...f, extra_fields: [...f.extra_fields, { label: "", value: "" }] }));
  const setField = (i, k, v) => setForm((f) => ({ ...f, extra_fields: f.extra_fields.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)) }));
  const removeField = (i) => setForm((f) => ({ ...f, extra_fields: f.extra_fields.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error("اسم المسجد مطلوب"); return; }
    const payload = { name: form.name.trim(), extra_fields: form.extra_fields.filter((f) => f.label.trim()) };
    try {
      if (editing) await api.put(`/mosques/${editing.id}`, payload);
      else await api.post("/mosques", payload);
      toast.success(editing ? "تم تحديث المسجد" : "تمت إضافة المسجد");
      setOpen(false); setForm(emptyForm); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("حذف هذا المسجد؟")) return;
    try { await api.delete(`/mosques/${id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (items === null) return <PageLoader />;

  return (
    <>
      <PageHeader title="المساجد" subtitle="قسم عام يعرض المساجد المسجّلة في مدينة حلب"
        action={canManage && (
          <Button data-testid="add-mosque-btn" onClick={openCreate} className="rounded-xl bg-green-deep text-white hover:opacity-90">
            <Plus className="me-2 h-4 w-4" /> إضافة مسجد
          </Button>
        )}
      />

      {items.length === 0 ? (
        <EmptyState icon={Landmark} title="لا توجد مساجد بعد" desc={canManage ? "أضف أول مسجد ليظهر لجميع المستخدمين." : "لم تتم إضافة أي مسجد حتى الآن."} />
      ) : (
        <div className="stagger grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((m, i) => {
            const t = cardTones[i % cardTones.length];
            return (
              <div key={m.id} data-testid={`mosque-${m.id}`} onClick={() => setDetail(m)}
                className={`card-hover group relative cursor-pointer overflow-hidden rounded-2xl border border-slate-100 bg-card p-6 soft-shadow ring-1 ${t.ring}`}>
                <div className={`pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b ${t.accent} to-transparent`} />
                <div className="relative flex items-start justify-between">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${t.chip}`}><Landmark className="h-6 w-6" /></div>
                  {canManage && (
                    <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <Button onClick={(e) => { e.stopPropagation(); openEdit(m); }} data-testid={`edit-mosque-${m.id}`} size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-400 hover:text-navy"><Pencil className="h-4 w-4" /></Button>
                      <Button onClick={(e) => { e.stopPropagation(); remove(m.id); }} data-testid={`del-mosque-${m.id}`} size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  )}
                </div>
                <h3 className="relative mt-4 font-heading text-lg font-bold text-navy">{m.name}</h3>
                {m.extra_fields?.length > 0 ? (
                  <div className="relative mt-3 space-y-1.5">
                    {m.extra_fields.slice(0, 3).map((f, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-slate-400">{f.label}</span>
                        <span className="truncate font-semibold text-slate-600">{f.value || "—"}</span>
                      </div>
                    ))}
                    {m.extra_fields.length > 3 && <p className="text-xs text-slate-400">+{m.extra_fields.length - 3} معلومة أخرى</p>}
                  </div>
                ) : <p className="relative mt-3 text-sm text-slate-400">اضغط لعرض التفاصيل</p>}
              </div>
            );
          })}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="mosque-detail">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-navy">
              <Landmark className="h-5 w-5 text-green-deep" /> {detail?.name}
            </DialogTitle>
          </DialogHeader>
          {detail?.extra_fields?.length > 0 ? (
            <div className="space-y-2">
              {detail.extra_fields.map((f, idx) => (
                <div key={idx} className="flex items-start justify-between gap-3 rounded-lg bg-slate-50 px-4 py-2.5 text-sm">
                  <span className="text-slate-500">{f.label}</span>
                  <span className="text-left font-semibold text-navy">{f.value || "—"}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="flex items-center gap-2 rounded-lg bg-slate-50 p-4 text-sm text-slate-400"><Info className="h-4 w-4" /> لا توجد معلومات إضافية لهذا المسجد.</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Create / Edit dialog */}
      {canManage && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-navy">{editing ? "تعديل المسجد" : "إضافة مسجد"}</DialogTitle></DialogHeader>
            <div className="grid gap-4 py-2">
              <div>
                <Label>اسم المسجد</Label>
                <Input data-testid="mosque-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="mt-1.5 rounded-lg" placeholder="مثال: مسجد حلب الكبير" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>معلومات إضافية (اختياري)</Label>
                  <Button data-testid="add-mosque-field" type="button" size="sm" variant="outline" onClick={addField} className="h-8 rounded-lg"><Plus className="me-1 h-3.5 w-3.5" /> حقل جديد</Button>
                </div>
                <p className="mt-1 text-xs text-slate-400">أضف أي بيانات مثل: الموقع، الإمام، أوقات الدروس... (قابلة للتوسّع مستقبلًا)</p>
                <div className="mt-2 space-y-2">
                  {form.extra_fields.length === 0 && <p className="rounded-lg border border-dashed border-slate-200 p-3 text-center text-xs text-slate-400">لا توجد حقول إضافية</p>}
                  {form.extra_fields.map((f, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input data-testid={`mosque-field-label-${i}`} value={f.label} onChange={(e) => setField(i, "label", e.target.value)} placeholder="اسم الحقل (مثال: الموقع)" className="rounded-lg" />
                      <Input data-testid={`mosque-field-value-${i}`} value={f.value} onChange={(e) => setField(i, "value", e.target.value)} placeholder="القيمة" className="rounded-lg" />
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeField(i)} className="h-9 w-9 shrink-0 p-0 text-red-400 hover:bg-red-50 hover:text-red-500"><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter><Button data-testid="mosque-save" onClick={submit} disabled={!form.name.trim()} className="rounded-xl bg-navy hover:bg-navy-light">حفظ</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
